import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { Context } from 'context/Context';
import HeliosClient from 'context/Helios';
import { useLocation, useParams } from 'react-router-dom';
import {
	ActionType,
	AppContext,
	Brands,
	CamaraScreenResult,
	ErrorImage,
	FieldStateKey,
	IndividualStateKey,
	OcrProcessingData,
	Photo,
	Validation,
} from 'context/interfaces';
import Breadcrumbs from 'components/Breadcrumbs';
import * as Sentry from '@sentry/browser';

import { deleteImageFromBucket, other, uploadPhoto } from '../../context/Utils';

import WarningScreen from '../../components/camera/Warning';
import imageCompression from 'browser-image-compression';

import { ReactComponent as HelpIcon } from 'assets/icons/info.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error-upload.svg';

import '@connect-assistance/connect-react-components-lib/dist/index.css';
import '../../assets/styles/screens/photos/Camera.scss';
import info from '../../assets/icons/i-info.svg';
import './CameraScreen.scss';
import ConfirmButton from 'components/common/ConfirmButton';
import HeimdallClient from 'context/Heimdall';
import Button from 'components/common/Button';
import Portal from 'components/portal';
import { PhotoId, getTooltipContents } from '@connect-assistance/connect-react-components-lib';
import { isBlurryImg } from 'shared/utils/blurryImage';
import { Tooltip } from '@material-ui/core';
import { Snackbar } from '@mui/material';
import { ReactComponent as LoadingIcon } from '../../assets/styles/imgs/loading.svg';
import '../../assets/styles/components/camera/Loading.scss';
import { Alert, AlertColor } from '@mui/lab';
import CpeClient, {
	Angles,
	Categories,
	CategoryCarRegistrationResults,
	CategoryDriverLicenseResults,
	CategoryLicensePlateResults,
	CategoryMarbeteResults,
	CategoryVehicleResults,
	CategoryVINResults,
	UploadImageResponse,
} from 'context/CPE';
import { ACTIVE_CPE, BRAND, isActiveIntegrationCpe, splitFullName } from 'shared/utils';

interface CameraProps {
	screenIndex: number;
}

// Inspired from: http://jsfiddle.net/4cwpLvae/
function ImageUploadThumbnail({
	photo,
	onChange,
	onPreviewClick,
	onHelpClick,
	setIsExample,
	photoPreview,
	loading,
	listErrors,
	onErrorClick,
}: {
	photo: Photo;
	onChange: any;
	onPreviewClick: any;
	onHelpClick: any;
	setIsExample: any;
	photoPreview: any;
	loading: any;
	listErrors: ErrorImage[];
	onErrorClick: any;
}): JSX.Element {
	if (photo.awsUrl) {
		if (loading?.loading && loading?.image === photo.id) {
			return (
				<div className="image-upload-thumbnail loading-image">
					<LoadingIcon data-testid="loading-icon" />
				</div>
			);
		} else {
			return (
				<div className="image-upload-thumbnail">
					<img
						src={photo.awsUrl}
						className="image-preview-thumbnail"
						alt={photo.description}
						onClick={() => onPreviewClick(photo)}
					/>
					{listErrors?.find((error) => error.id === photo.id) && <ErrorIcon className="error-icon-upload" />}
				</div>
			);
		}
	} else if (photoPreview && photoPreview.length > 0) {
		const previewPhoto = photoPreview.find((preview: any) => preview.id === photo.id);
		const error = listErrors?.find((error) => error.id === photo.id);
		if (previewPhoto) {
			if (loading?.loading && loading?.image === photo.description) {
				return (
					<div className="image-upload-thumbnail loading-image">
						<LoadingIcon data-testid="loading-icon" />
					</div>
				);
			} else if (error) {
				return (
					<div className="image-upload-thumbnail" onClick={() => onErrorClick(photo.id)}>
						<img src={previewPhoto.url} className="image-preview-thumbnail" alt={photo.description} />
						<ErrorIcon className="error-icon-upload" />
					</div>
				);
			} else {
				return (
					<div className="image-upload-thumbnail">
						<img
							src={previewPhoto.url}
							className="image-preview-thumbnail"
							alt={photo.description}
							onClick={() => onPreviewClick(photo)}
						/>
					</div>
				);
			}
		}
	}
	const htmlId = `photo-${photo.id}`;
	return (
		<div className="image-upload-thumbnail">
			<div
				className="custom-file-upload"
				onClick={() => {
					setIsExample(false);
					onHelpClick(photo);
				}}
			>
				<div className="image-container">
					<img src={photo.placeholder} className="camera-plus-icon" alt="camera icon" />
					{photo.description.length > 50 && (
						<Tooltip title={photo.description} classes={{ popper: 'custom-tooltip' }}>
							<img src={info} className="info-icon" alt="info icon" />
						</Tooltip>
					)}
				</div>
				<span title={photo.description}>{photo.description}</span>
			</div>
			<div
				className="ayuda"
				onClick={() => {
					setIsExample(true);
					onHelpClick(photo);
				}}
			>
				<HelpIcon />
				<span>Ejemplo</span>
			</div>
			{(photo.useGallery === undefined || (photo.useGallery !== undefined && !photo.useGallery)) && (
				<input
					id={htmlId}
					type="file"
					accept="image/*;capture=camera"
					capture="environment"
					onChange={(e) => onChange(photo, e)}
				/>
			)}

			{photo.useGallery && (
				<input
					id={htmlId}
					type="file"
					accept="image/*,video/*"
					capture="environment"
					onChange={(e) => onChange(photo, e)}
				/>
			)}
		</div>
	);
}

function HelpModal({ photo, onClose, isExample }: { photo: Photo; onClose: any; isExample: boolean }): JSX.Element {
	const contents = getTooltipContents(photo.id as PhotoId);
	const defaultContents = {
		title: 'Agrega una nueva foto',
		imgUrl: other,
		paragraph: 'En esta sección puedes agregar fotos adicionales que consideres necesarias.',
	};

	const closeModal = (htmlId: string) => {
		onClose();
		if (!isExample) {
			document.getElementById(htmlId)?.click();
		}
	};

	const htmlId = `photo-${photo.id}`;

	return (
		<Portal>
			<div className="photo-help-container" onClick={onClose}>
				<div
					className="modal"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<h2>{contents?.title || defaultContents.title}</h2>
					<img src={contents?.imgUrl || defaultContents.imgUrl} alt={contents?.title || defaultContents.title} />
					{contents?.paragraph || <p>{defaultContents.paragraph}</p>}
					<Button
						// eslint-disable-next-line @typescript-eslint/no-unused-vars
						onClick={(e) => {
							closeModal(htmlId);
						}}
					>
						Entendido
					</Button>
				</div>
			</div>
		</Portal>
	);
}

function FullScreenPreview({ photo, onRemove, onExit }: { photo: Photo; onRemove: any; onExit: any }): JSX.Element {
	return (
		<div className="full-screen-preview">
			<img className="preview-photo" src={photo.awsUrl} alt={photo.description + ' preview'} />
			<div className="action-buttons">
				<Button danger onClick={onRemove}>
					Eliminar
				</Button>
				<Button onClick={onExit}>Ok</Button>
			</div>
		</div>
	);
}

export const savePhoto = async (
	event: any,
	setValidationError: any,
	photo: Photo,
	eventId: number,
	serviceId: string,
	inspectionId: string,
	cpeClaimId: string,
	cpeInspectionId: string,
	cpeIsSync: boolean,
	loading: any,
	setLoading: any,
	listError: ErrorImage[],
	setListError: any,
	forceUpload?: boolean
): Promise<CamaraScreenResult | null> => {
	const imageFile = event.target.files[0];
	const compressedFile = await imageCompression(imageFile, {
		maxSizeMB: 1,
		maxWidthOrHeight: 1920,
		useWebWorker: true,
	});

	const dataURI = await imageCompression.getDataUrlFromFile(compressedFile);
	const awsUrl = await uploadPhoto({ ...photo!, dataUrl: dataURI }, eventId, serviceId, inspectionId, forceUpload);
	const cpeImageId =
		isActiveIntegrationCpe() && cpeIsSync
			? await checkValidationCpe(
					eventId,
					photo,
					imageFile,
					setValidationError,
					cpeClaimId,
					cpeInspectionId,
					forceUpload ?? false,
					listError,
					setListError,
					event
			  )
			: null;
	const valid =
		isActiveIntegrationCpe() && cpeIsSync
			? cpeImageId === 'NO_VALIDATION' || cpeImageId != null
			: forceUpload === false
			? await checkValidation(photo, awsUrl, setValidationError, imageFile, listError, setListError, event)
			: true;
	if (!valid && awsUrl) await deleteImageFromBucket({ imageUrl: awsUrl, eventId, serviceId, inspectionId });
	if (awsUrl && valid) {
		return {
			imageId: cpeImageId,
			imageUrl: awsUrl,
		};
	}

	return null;
};

// Request API Cpe Validation AI
export const checkValidationCpe = async (
	eventRecord: number,
	photo: Photo,
	imageOrigin: File,
	setValidationError: React.Dispatch<React.SetStateAction<Validation | 'IS_BLURRY' | null>>,
	cpeClaimId: string,
	cpeInspectionId: string,
	forceUpload: boolean,
	listError: ErrorImage[],
	setListError: any,
	eventFile: any
): Promise<string | null> => {
	const cpeClient = new CpeClient();
	const validation = photo.validations?.[0];
	if (!validation) {
		console.warn('No validation specified');

		return 'NO_VALIDATION';
	}

	const validationMapping: Record<any, { category: Categories }> = {
		[Validation.HAS_VEHICLE]: { category: Categories.VEHICLE },
		[Validation.EXTRACT_PLATE]: { category: Categories.LICENSE_PLATE },
		[Validation.DRIVER_LICENSE]: { category: Categories.DRIVER_LICENSE },
		[Validation.MARBETE]: { category: Categories.MARBETE },
		[Validation.EXTRACT_VIN]: { category: Categories.VIN_NUMBER },
		[Validation.EXTRACT_CAR_REGISTRATION]: { category: Categories.CAR_REGISTRATION },
		[Validation.PUSH_ODOMETER]: { category: Categories.ODOMETER },
		[Validation.PUSH_VEHICLE_DAMAGE]: { category: Categories.VEHICLE_DAMAGE },
		[Validation.ADDITIONAL_DRIVERS_LICENSE]: { category: Categories.ADDITIONAL_DRIVERS_LICENSE },
		[Validation.BANK_STATEMENT]: {
			category: Categories.BANK_STATEMENT,
		},
		[Validation.BILL_OF_SALE]: {
			category: Categories.BILL_OF_SALE,
		},
		[Validation.BIRTH_CERTIFICATE]: {
			category: Categories.BIRTH_CERTIFICATE,
		},
	};

	const validationConfig = validationMapping[validation];

	if (!validationConfig) {
		console.warn('Unknown validation', validation);

		return 'NO_VALIDATION';
	}

	try {
		if (cpeClaimId?.length > 0) {
			const result = await cpeClient.sendImageValidation(
				[imageOrigin],
				cpeClaimId,
				validationConfig.category,
				forceUpload
			);
			console.log('Results Cpe:', result);

			//save id image cpe local
			localStorage.setItem(`cpe-${photo.id}`, result.id);

			//if force upload is true don't parse results
			if (forceUpload) {
				return result.id;
			}

			const parsedResult = await parseResultsCpe(photo, validationConfig.category, result);
			if (parsedResult) {
				return result.id;
			}

			return null;
		} else {
			console.error('Error fetch claim');
			const err = listError;
			await addError(err, photo, setListError, validation, eventFile);
			return null;
		}
	} catch (error) {
		console.error('Error during validation:', error);
		localStorage.removeItem(`cpe-${photo.id}`);
		const err = listError;
		if (error.message === 'Angle_Incorrect') {
			await addError(err, photo, setListError, Validation.INVALID_VEHICLE_ZONE, eventFile);
		} else {
			await addError(err, photo, setListError, validation, eventFile);
		}
		return null;
	}
};

export const addError = async (
	err: ErrorImage[],
	photo: Photo,
	setListError: any,
	validation: Validation | 'IS_BLURRY',
	eventFile: any
) => {
	if (err.find((e) => e.id === photo?.id) === undefined) {
		err.push({ id: photo.id, error: validation, photo: photo, event: eventFile });
		setListError(err);
	}
};

//Parse Results Cpe
export const parseResultsCpe = async (
	photo: Photo,
	category: Categories,
	result: UploadImageResponse
): Promise<boolean> => {
	if (!result?.mlResults) {
		return true;
	}

	const ocrDataStr = localStorage.getItem('ocrData') ?? null;
	const ocrStorage: OcrProcessingData = ocrDataStr
		? JSON.parse(ocrDataStr)
		: {
				fullName: null,
				dateOfBirth: null,
				driverLicense: null,
				licensePlate: null,
				vinNumber: null,
				marbete: null,
				marbeteDateExpiry: null,
				carRegistration: null,
				titleNumber: null,
				make: null,
				model: null,
				year: null,
				color: null,
				effectiveDate: null,
				expirationDate: null,
		  };

	switch (category) {
		case Categories.VEHICLE: {
			const vehicleResults: CategoryVehicleResults = JSON.parse(result.mlResults);
			console.log('Parsed Vehicle Results:', vehicleResults);

			const anglesMapping: Record<any, Angles> = {
				[PhotoId.INSURED_VEHICLE_DRIVER_SIDE]: Angles.left,
				[PhotoId.INJURED_VEHICLE_DRIVER_SIDE]: Angles.left,
				[PhotoId.INSURED_VEHICLE_PASSENGER_SIDE]: Angles.right,
				[PhotoId.INJURED_VEHICLE_PASSENGER_SIDE]: Angles.right,
				[PhotoId.INSURED_VEHICLE_BACK]: Angles.rear,
				[PhotoId.INJURED_VEHICLE_BACK]: Angles.rear,
				[PhotoId.INSURED_VEHICLE_FRONT]: Angles.front,
				[PhotoId.INJURED_VEHICLE_FRONT]: Angles.front,
			};

			// Implement specific logic
			const angle = anglesMapping[photo.id];
			if (vehicleResults.angle !== angle) {
				//delete cpe
				await new CpeClient().deleteImage(result.claimId, result.id);
				throw new Error('Angle_Incorrect');
			}
			break;
		}
		case Categories.LICENSE_PLATE: {
			const plateResults: CategoryLicensePlateResults = JSON.parse(result.mlResults);
			ocrStorage.licensePlate =
				plateResults.licensePlateNumber && plateResults.licensePlateNumber.length > 0
					? plateResults.licensePlateNumber.trim()
					: ocrStorage.licensePlate;

			console.log('Parsed License Plate Results:', plateResults);
			break;
		}
		case Categories.DRIVER_LICENSE: {
			const licenseResults: CategoryDriverLicenseResults = JSON.parse(result.mlResults);
			ocrStorage.driverLicense =
				licenseResults.licenseNumber && licenseResults.licenseNumber.length > 0
					? licenseResults.licenseNumber.trim()
					: ocrStorage.driverLicense;
			ocrStorage.fullName =
				licenseResults.fullName && licenseResults.fullName.length > 0 ? licenseResults.fullName : ocrStorage.fullName;

			console.log('Parsed Driver License Results:', licenseResults);
			break;
		}
		case Categories.MARBETE: {
			const marbeteResults: CategoryMarbeteResults = JSON.parse(result.mlResults);
			ocrStorage.marbete =
				marbeteResults.marbeteNumber && marbeteResults.marbeteNumber.length > 0
					? marbeteResults.marbeteNumber.trim()
					: ocrStorage.marbete;

			console.log('Parsed Marbete Results:', marbeteResults);
			break;
		}
		case Categories.VIN_NUMBER: {
			const vinResults: CategoryVINResults = JSON.parse(result.mlResults);
			ocrStorage.vinNumber =
				vinResults.vinNumber && vinResults.vinNumber.length > 0 ? vinResults.vinNumber.trim() : ocrStorage.vinNumber;

			console.log('Parsed VIN Results:', vinResults);

			break;
		}
		case Categories.CAR_REGISTRATION: {
			const carRegisResults: CategoryCarRegistrationResults = JSON.parse(result.mlResults);
			ocrStorage.fullName =
				carRegisResults.fullName && carRegisResults.fullName.length > 0
					? carRegisResults.fullName.trim()
					: ocrStorage.fullName;
			ocrStorage.make =
				carRegisResults.make && carRegisResults.make.length > 0 ? carRegisResults.make : ocrStorage.make;
			ocrStorage.model =
				carRegisResults.model && carRegisResults.model.length > 0 ? carRegisResults.model : ocrStorage.model;
			ocrStorage.year =
				carRegisResults.year && carRegisResults.year.length > 0 ? carRegisResults.year : ocrStorage.year;
			ocrStorage.color =
				carRegisResults.color && carRegisResults.color.length > 0 ? carRegisResults.color : ocrStorage.color;
			ocrStorage.vinNumber =
				carRegisResults.vin && carRegisResults.vin.length > 0 ? carRegisResults.vin.trim() : ocrStorage.vinNumber;
			ocrStorage.registrationNumber =
				carRegisResults.registrationNumber && carRegisResults.registrationNumber.length > 0
					? carRegisResults.registrationNumber.trim()
					: ocrStorage.registrationNumber;
			ocrStorage.licensePlate =
				carRegisResults.licensePlate && carRegisResults.licensePlate.length > 0
					? carRegisResults.licensePlate.trim()
					: ocrStorage.licensePlate;
			ocrStorage.marbeteDateExpiry =
				carRegisResults.expirationDate && carRegisResults.expirationDate.length > 0
					? carRegisResults.expirationDate.trim()
					: ocrStorage.marbeteDateExpiry;

			console.log('Parsed Car Register Results:', carRegisResults, ocrStorage);
			break;
		}
		default: {
			console.warn('Unknown category for parsing', category);
		}
	}

	localStorage.setItem('ocrData', JSON.stringify(ocrStorage));

	return true;
};

/** Makes requests to Heimdall per photo.validations */
export const checkValidation = async (
	photo: Photo,
	awsUrl: any,
	setValidationError: any,
	originalImg: File,
	listError: ErrorImage[],
	setListError: any,
	eventFile: any
): Promise<boolean> => {
	// Check if the image is blurry before newtwork based validations
	const isBlurry = await isBlurryImg(originalImg);
	if (isBlurry) {
		const err = listError;
		await addError(err, photo, setListError, 'IS_BLURRY', eventFile);
		return !isBlurry;
	}

	const client = new HeimdallClient();
	const promises: Promise<any>[] = photo.validations.map((validation) => {
		switch (validation) {
			case Validation.HAS_DOCUMENT:
				return client.isDocument(awsUrl);
			case Validation.HAS_VEHICLE:
				return client.isVehicle(awsUrl);
			case Validation.EXTRACT_PLATE:
				return client.extractPlate(awsUrl);
			// case Validation.EXTRACT_VIN:
			// 	return client.extractVin(awsUrl);
			case Validation.HAS_LEGIBLE_LICENSE:
				return client.hasLegibleLicense(awsUrl);
			default:
				console.warn('Unknown validation', validation);
				return Promise.resolve(true);
		}
	});
	const results = await Promise.all(promises);

	// using .some to "short-circuit" and just set error to first found
	const foundErrors = results.some((result, index) => {
		if (result === false || result === null) {
			const err = listError;
			addError(err, photo, setListError, photo.validations[index], eventFile);
			return true; // found error
		}
		return false;
	});
	return !foundErrors;
};

const CameraScreen = ({ screenIndex }: CameraProps): ReactElement => {
	const ctx = useContext(Context) as AppContext;
	const { photos, ClaimantType, cpeInspectionIsSync } = ctx;
	const { id } = useParams<{ id: string }>();
	const [validationError, setValidationError] = useState<Validation | null | 'IS_BLURRY'>(null);
	const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null);
	const [helpModalId, setHelpModalId] = useState<Photo | null>(null);
	const [isExample, setIsExample] = useState<boolean>(false);
	const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState<{ id: string; url: string }[]>([]);
	const [uploadPhotoMessage, setUploadPhotoMessage] = useState<{
		showAlertPhoto: boolean;
		alertMessagePhoto: string;
		alertTypePhoto: AlertColor | undefined;
	}>({
		showAlertPhoto: false,
		alertMessagePhoto: '',
		alertTypePhoto: 'success',
	});
	const [listErrors, setListErrors] = useState<ErrorImage[]>([]);
	const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
	const [typeMessage, setTypeMessage] = useState<AlertColor | undefined>('success');
	const [alertMessage, setAlertMessage] = useState<string>('');
	const [finallyUpload, setFinallyUpload] = useState<boolean>(false);
	const [loading, setLoading] = useState<any>({ loading: false, image: '' });
	const {
		state: { sectionId },
	} = useLocation<{ sectionId: string }>();
	const [uploadPhoto, setUploadPhoto] = useState<Photo | undefined>(undefined);
	const [uploadEvent, setUploadEvent] = useState<any>();

	const onAddPhoto = async (photoId: string, awsUrl: string, cpeImageId: string | null): Promise<any> => {
		ctx.dispatch({
			type: ActionType.ADD_PHOTO,
			data: { photoId, awsUrl, cpeImageId },
		});
		return new HeliosClient().saveInspection(ctx, id);
	};

	const removePhoto = async (photoId: string): Promise<void> => {
		const photo = ctx.photos.find((photo) => photo.id === photoId);
		if (!photo) {
			return;
		}
		setUploadedPhotoUrls((prevPhotos) => prevPhotos.filter((preview) => preview.id !== photoId));

		await deleteImageFromBucket({
			eventId: +ctx.eventId!,
			inspectionId: ctx.inspectionId!,
			imageUrl: photo.awsUrl!,
			serviceId: ctx.serviceId!,
		});

		if (isActiveIntegrationCpe() && cpeInspectionIsSync === true) {
			try {
				const cpeClient = new CpeClient();

				const claim = await cpeClient.getClaim(String(ctx.eventId));

				if (claim?.length > 0) {
					const idImageCpe = localStorage.getItem(`cpe-${photoId}`);

					if (idImageCpe) {
						await cpeClient.deleteImage(claim[0].id, idImageCpe);
						localStorage.removeItem(`cpe-${photoId}`);
					}
				}
			} catch (error) {
				console.error('Delete Image Cpe:', error);
			}
		}

		ctx.dispatch({ type: ActionType.DELETE_PHOTO, data: photoId });
		new HeliosClient().saveInspection(ctx, id);
		setPreviewPhoto(null);
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const onChange = async (photo: Photo, event: any, forceUpload = false) => {
		if (!event.target.files) {
			return;
		}

		setUploadPhoto(photo);
		setUploadEvent(event);

		const imageFile = event.target.files[0];
		const reader = new FileReader();

		reader.onload = async (e) => {
			if (!e.target || !e.target.result) {
				return;
			}

			const dataUrl = e.target.result as string;

			const img = new Image();
			img.src = dataUrl;

			img.onload = () => {
				setUploadedPhotoUrls((prevUploadedPhotoUrls) => [...prevUploadedPhotoUrls, { id: photo.id, url: dataUrl }]);
			};
		};

		reader.readAsDataURL(imageFile);
		const interval = changeMessageLoading();
		setFinallyUpload(false);
		const load = {
			loading: true,
			image: photo.description,
			error: { validation: '', image: '' },
		};
		try {
			setLoading(load);
			const photoResult = await savePhoto(
				event,
				setValidationError,
				photo,
				ctx.eventId!,
				ctx.serviceId!,
				ctx.inspectionId!,
				ctx.cpeId!,
				ctx.cpeInspectionId!,
				ctx.cpeInspectionIsSync ?? false,
				loading,
				setLoading,
				listErrors,
				setListErrors,
				forceUpload
			);
			if (photoResult && photoResult.imageUrl) {
				await onAddPhoto(photo.id, photoResult.imageUrl, photoResult.imageId);

				//Update OCR Data
				const ocrDataStr = localStorage.getItem('ocrData') ?? null;
				if (ocrDataStr) {
					const ocrStorage: OcrProcessingData = JSON.parse(ocrDataStr);
					ctx.dispatch({ type: ActionType.SET_OCR_DATA_BY_THE_PROCESS, data: ocrStorage });
					ctx.dispatch({ type: ActionType.SET_OCR_DATA_BY_THE_CLIENT, data: ocrStorage });

					handleUpateFormState(ocrStorage);
				}
			}
		} catch (error) {
			load.loading = false;
			setLoading(load);
			Sentry.captureException(error);
		} finally {
			load.loading = false;
			setLoading(load);
			clearInterval(interval);
			setFinallyUpload(true);
		}
	};

	const handleWarningButton = (photo: Photo, listError: ErrorImage[], setListError: any): void => {
		setListError(listError.filter((e) => e.id !== photo?.id));
		setUploadedPhotoUrls(uploadedPhotoUrls.filter((e) => e.id !== photo?.id));
		setValidationError(null);
		setFinallyUpload(!finallyUpload);
	};

	useEffect(() => {
		if (listErrors.length > 0) {
			const msgError = `Detectamos errores en algunas imágenes. Toca la <b>'X'</b> en cada una para revisarlas y corregirlas.`;
			setUploadPhotoMessage({
				...uploadPhotoMessage,
				showAlertPhoto: true,
				alertMessagePhoto: msgError,
				alertTypePhoto: 'error',
			});
		} else {
			setUploadPhotoMessage({
				...uploadPhotoMessage,
				showAlertPhoto: false,
				alertMessagePhoto: '',
				alertTypePhoto: 'success',
			});
		}
	}, [finallyUpload]);

	const changeMessageLoading = () => {
		const messages: string[] = ['Validando Fotografía', 'Subiendo Fotografía', 'Procesando Fotografía'];
		let indexMessage = 0;
		const interval = setInterval(() => {
			if (indexMessage < messages.length - 1) {
				indexMessage += 1;
			}
		}, 5000);

		return interval;
	};

	const filteredPhotos = photos.filter((photo) => photo.sectionId === sectionId);
	const canContinue = filteredPhotos.every((photo) => (photo.required ? Boolean(photo.awsUrl) : true));

	useEffect(() => {
		setShowSnackbar(false);
		if (uploadPhotoMessage.alertTypePhoto && uploadPhotoMessage.alertMessagePhoto) {
			setShowSnackbar(true);
			setTypeMessage(uploadPhotoMessage.alertTypePhoto);
			setAlertMessage(uploadPhotoMessage.alertMessagePhoto);
		}
	}, [uploadPhotoMessage]);

	const handleSnackBarClearMessage = () => {
		setShowSnackbar(false);
		setFinallyUpload(false);
	};

	useEffect(() => {
		if (showSnackbar && typeMessage === 'success') {
			const timer = setTimeout(() => {
				handleSnackBarClearMessage();
			}, 5000);

			return () => clearTimeout(timer);
		}

		console.log('screenIndex', screenIndex);
	}, [showSnackbar, typeMessage]);

	const handleUpateFormState = (ocrData: OcrProcessingData): void => {
		if (ocrData !== null && ocrData !== undefined) {
			const currentStateKey =
				Brands.CSM === BRAND
					? IndividualStateKey.INJURED
					: Brands.MULTINATIONAL === BRAND || Brands.PREMIER === BRAND
					? ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
						? IndividualStateKey.INSURED
						: IndividualStateKey.INJURED
					: IndividualStateKey.INSURED;

			Object.entries(ocrData).forEach(([key, value]) => {
				if (key === 'fullName' && value.length > 0) {
					// First Name
					if (ocrData.fullName && ocrData.fullName.length > 0) {
						const resultFullName = splitFullName(ocrData.fullName);
						const fistName = resultFullName?.FirstName.length > 0 ? resultFullName.FirstName : ocrData.fullName;

						ctx.dispatch({
							type: ActionType.SET_FIELD_VALUE,
							data: {
								stateKey: currentStateKey,
								fieldStateKey: FieldStateKey.FIRST_NAME,
								value: fistName,
							},
						});

						ctx.dispatch({
							type: ActionType.SET_FIELD_VALUE,
							data: {
								stateKey: currentStateKey,
								fieldStateKey: FieldStateKey.DRIVER_NAME,
								value: fistName,
							},
						});

						const lastName = resultFullName?.LastName.length > 0 ? resultFullName.LastName : '';
						ctx.dispatch({
							type: ActionType.SET_FIELD_VALUE,
							data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.LAST_NAME, value: lastName },
						});

						ctx.dispatch({
							type: ActionType.SET_FIELD_VALUE,
							data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.DRIVER_LASTNAME, value: lastName },
						});
					}
				} else if (key === 'licensePlate' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.PLATE, value },
					});
				} else if (key === 'make' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.MAKE, value },
					});
				} else if (key === 'model' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.MODEL, value },
					});
				} else if (key === 'year' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.YEAR, value },
					});
				} else if (key === 'color' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.COLOR, value },
					});
				} else if (key === 'driverLicense' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.LICENSE, value },
					});

					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.DRIVER_LICENSE, value },
					});
				} else if (key === 'marbeteDateExpiry' && value.length > 0) {
					ctx.dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: currentStateKey, fieldStateKey: FieldStateKey.DRIVER_MARBETE, value },
					});
				}
			});
		}
	};

	const onErrorClick = (id: any): void => {
		const error = listErrors?.find((error) => error.id === id);
		if (error) {
			setUploadEvent(error?.event);
			setUploadPhoto(error?.photo);
			setValidationError(error?.error);
		}
	};

	return (
		<div className="simple-camera-screen">
			<Breadcrumbs currentStep={screenIndex} />

			<div className="photos-container">
				{filteredPhotos.map((photo) => (
					<ImageUploadThumbnail
						key={photo.id}
						photo={photo}
						onChange={onChange}
						onPreviewClick={setPreviewPhoto}
						onHelpClick={setHelpModalId}
						setIsExample={setIsExample}
						photoPreview={uploadedPhotoUrls}
						loading={loading}
						listErrors={listErrors}
						onErrorClick={onErrorClick}
					/>
				))}
			</div>
			<ConfirmButton disabled={!canContinue} to={'/' + id + '/photo/sections'} />
			{validationError && (
				<div className="warnings">
					{validationError === Validation.HAS_DOCUMENT && (
						<WarningScreen
							title="Documento no detectado"
							message="Parece que la foto que tomaste no corresponde a un documento o se encuentra borrosa"
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
						/>
					)}
					{validationError === Validation.DRIVER_LICENSE && (
						<WarningScreen
							title="Licencia no detectada"
							message="Parece que la foto que tomaste no corresponde a una licencia de conducir o se encuentra borrosa"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
					{validationError === Validation.MARBETE && (
						<WarningScreen
							title="Marbete no detectado"
							message="Parece que la foto que tomaste no corresponde a un marbete o se encuentra borrosa"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
					{validationError === Validation.HAS_VEHICLE && (
						<WarningScreen
							title="Vehículo no detectado"
							message="Parece que la foto que tomaste no corresponde al lado correcto del vehículo o se encuentra borrosa"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
					{validationError === Validation.EXTRACT_PLATE && (
						<WarningScreen
							title="Placa no detectada"
							message="Parece que la foto que tomaste no corresponde a una placa o se encuentra borrosa"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
					{validationError === Validation.EXTRACT_VIN && (
						<WarningScreen
							title="VIN no detectada"
							message="Parece que la foto que tomaste no corresponde a un VIN o se encuentra borrosa"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
					{validationError === Validation.EXTRACT_CAR_REGISTRATION && (
						<WarningScreen
							title="Licencia del vehículo no detectada"
							message="Parece que la foto que tomaste no corresponde a una Licencia del vehículo o se encuentra borrosa"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
					{validationError === Validation.HAS_LEGIBLE_LICENSE && (
						<WarningScreen
							title="Licencia no detectada"
							message="Parece que la foto que tomaste no corresponde a un licencia o se encuentra borrosa"
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
						/>
					)}
					{validationError === 'IS_BLURRY' && (
						<WarningScreen
							title="Imagen borrosa"
							message="Parece que la foto que tomaste se encuentra borrosa"
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
						/>
					)}
					{validationError === 'INVALID_VEHICLE_ZONE' && (
						<WarningScreen
							title="Zona no válida"
							message="Parece que la foto que tomaste no corresponde a la zona solicitada"
							showButtonForce={isActiveIntegrationCpe()}
							handleClick={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
							}}
							handleForceUpload={() => {
								handleWarningButton(uploadPhoto as Photo, listErrors, setListErrors);
								onChange(uploadPhoto as Photo, uploadEvent, true); //force upload CPE
							}}
						/>
					)}
				</div>
			)}
			{previewPhoto && (
				<FullScreenPreview
					photo={previewPhoto}
					onRemove={() => removePhoto(previewPhoto.id)}
					onExit={() => setPreviewPhoto(null)}
				/>
			)}
			{helpModalId && <HelpModal photo={helpModalId} onClose={() => setHelpModalId(null)} isExample={isExample} />}

			<Snackbar
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				open={showSnackbar}
			>
				{typeMessage !== 'success' ? (
					<Alert severity={typeMessage}>
						<div dangerouslySetInnerHTML={{ __html: alertMessage }} />
					</Alert>
				) : (
					<Alert onClose={() => handleSnackBarClearMessage()} severity={typeMessage}>
						<div dangerouslySetInnerHTML={{ __html: alertMessage }} />
					</Alert>
				)}
			</Snackbar>
		</div>
	);
};

export default CameraScreen;
